import {noop} from '@github-ui/noop'
import type {ValidationResult} from '@github-ui/entity-validators'
import {Box, Flash, Text, Tooltip, useRefObjectAsForwardedRef} from '@primer/react'
import {
  MarkdownEditor as PrimerMarkdownEditor,
  type MarkdownEditorHandle,
  type MarkdownEditorProps as PrimerMarkdownEditorProps,
} from '@primer/react/experimental'

import {MarkdownProvider, useMarkdownContext} from './MarkdownContext'
import {useMarkdownSuggestions} from './use-markdown-suggestions'
import {AlertFillIcon, DiffIgnoredIcon, type Icon} from '@primer/octicons-react'
import {
  createContext,
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {emojiList} from './emojis'
import {ERRORS, LABELS} from './constants'
import {SlashCommandsProvider} from './SlashCommandsProvider'
import {AddTasklistButton} from './components/AddTaskListButton'
import {acceptedFileTypes} from './api/file-upload'
import type {MarkdownSubject} from './subject'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

export type MarkdownEditorSettings = {
  pasteUrlsAsPlainText: boolean
  useMonospaceFont: boolean
}

export type ViewMode = 'preview' | 'edit'

export type Props = Omit<
  PrimerMarkdownEditorProps,
  | 'emojiSuggestions'
  | 'mentionSuggestions'
  | 'referenceSuggestions'
  | 'onUploadFile'
  | 'acceptedFileTypes'
  | 'savedReplies'
  | 'onRenderPreview'
  | 'children'
> & {
  label?: string
  showLabel?: boolean
  subject?: MarkdownSubject
  onSave?: () => void
  onCancel?: () => void
  validationResult?: ValidationResult
  ['data-testid']?: string
  saveButtonText?: string
  saveButtonTrailingIcon?: boolean
  actions?: React.ReactNode
  userSettings?: MarkdownEditorSettings
  /** Block saving due to stale content. */
  contentIsStale?: boolean
  fileUploadsEnabled?: boolean
  buttonSize?: 'small' | 'medium'
}

type ReadonlyMarkdownProps = {
  reason: string
  ['data-testid']?: string
  icon?: Icon
}

const SlashCommandButtonContext = createContext<(() => void) | null>(null)

export const MarkdownEditor = forwardRef((props: Props, ref: ForwardedRef<MarkdownEditorHandle>): ReactElement => {
  return (
    <Box sx={{mb: 0}} data-testid={props['data-testid']}>
      <MarkdownProvider subject={props.subject}>
        <MarkdownEditorWrapper ref={ref} {...props} />
      </MarkdownProvider>
    </Box>
  )
})

MarkdownEditor.displayName = 'MarkdownEditor'

const MarkdownEditorWrapper = forwardRef(
  (
    {
      ['aria-describedby']: ariaDescribedby,
      label,
      showLabel,
      disabled = false,
      subject,
      viewMode,
      onChangeViewMode,
      value,
      placeholder,
      onChange,
      onSave,
      onCancel,
      validationResult,
      saveButtonText = 'Save',
      saveButtonTrailingIcon = true,
      actions,
      userSettings: optionConfig,
      contentIsStale,
      fileUploadsEnabled = true,
      minHeightLines,
      maxHeightLines,
      sx,
      onPrimaryAction,
      buttonSize,
    }: Props,
    forwardedRef: ForwardedRef<MarkdownEditorHandle>,
  ): ReactElement => {
    const {mentions, references, savedReplies} = useMarkdownSuggestions()
    const {renderPreview, uploadFile} = useMarkdownContext()
    const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(viewMode ?? 'edit')
    const [showContentIsStaleWarning, setShowContentIsStaleWarning] = useState(false)
    const ref = useRef<MarkdownEditorHandle>(null)
    useRefObjectAsForwardedRef(forwardedRef, ref)
    const warningRef = useRef<HTMLDivElement | null>(null)
    const isProjectSubject = subject && 'projectId' in subject

    const showAddTaskListButton =
      useFeatureFlag('tasklist_block') && !isProjectSubject && subject?.repository !== undefined

    useEffect(() => {
      setShowContentIsStaleWarning(contentIsStale ?? false)
      return () => {
        setShowContentIsStaleWarning(false)
      }
    }, [contentIsStale])

    const onSaveAction = useMemo(() => {
      if (showContentIsStaleWarning) {
        // This effectively disables/hides the save button
        return noop
      }
      return () => {
        onSave?.()
        setCurrentViewMode('edit')
      }
    }, [onSave, showContentIsStaleWarning])

    const repoDatabaseId = isProjectSubject ? undefined : subject?.repository?.databaseId
    const subjectDatabaseId = isProjectSubject ? undefined : subject?.id?.databaseId
    useEffect(() => {
      setCurrentViewMode('edit')
    }, [repoDatabaseId, subjectDatabaseId])

    useEffect(() => {
      if (viewMode) {
        setCurrentViewMode(viewMode)
      }
    }, [viewMode])

    const onChangeViewModeInternal = useCallback(
      (v: ViewMode) => {
        setCurrentViewMode(v)
        if (onChangeViewMode) {
          onChangeViewMode(v)
        }
      },
      [onChangeViewMode],
    )

    const onStartSlashCommand = useCallback(() => {
      ref.current?.focus()
      try {
        document.execCommand('insertText', false, '/')
      } catch {
        // this is a pretty naive approach and may fail in some environments (particularly JSDom)
        // but it will work in most cases and it's not worth reimplementing the full robust effort
        // used by the MarkdownEditor internals
      }
    }, [ref])

    // Only scroll to the warning banner if otherwise the user would not be able to see it
    const warningBannerPosition = 173
    if (showContentIsStaleWarning && warningRef.current !== null && window.scrollY > warningBannerPosition) {
      warningRef.current.scrollIntoView()
    }

    // Suspense is here to avoid suspending the broader component tree when being rendered on the server with SSR
    // this is a workaround for https://github.com/github/primer/issues/2090
    const editor = (
      <Suspense>
        {showContentIsStaleWarning && (
          <Flash ref={warningRef} sx={{mb: 2}} variant="danger">
            {ERRORS.staleContent}
          </Flash>
        )}
        <PrimerMarkdownEditor
          aria-describedby={ariaDescribedby}
          disabled={disabled}
          value={value}
          placeholder={placeholder ?? LABELS.leaveComment}
          emojiSuggestions={emojiList}
          mentionSuggestions={mentions}
          referenceSuggestions={references}
          viewMode={currentViewMode}
          onChangeViewMode={onChangeViewModeInternal}
          onChange={onChange}
          onRenderPreview={renderPreview}
          onUploadFile={fileUploadsEnabled ? uploadFile : undefined}
          acceptedFileTypes={
            fileUploadsEnabled && !isProjectSubject ? acceptedFileTypes(subject?.repository !== undefined) : undefined
          }
          onPrimaryAction={() => {
            onSaveAction()
            onPrimaryAction?.()
          }}
          ref={ref}
          pasteUrlsAsPlainText={optionConfig?.pasteUrlsAsPlainText || false}
          savedReplies={savedReplies}
          monospace={optionConfig?.useMonospaceFont || false}
          minHeightLines={minHeightLines}
          maxHeightLines={maxHeightLines}
          sx={sx}
        >
          <PrimerMarkdownEditor.Label visuallyHidden={!showLabel}>
            {label ?? 'Markdown Editor'}
          </PrimerMarkdownEditor.Label>
          <PrimerMarkdownEditor.Actions>
            {actions}
            {onCancel && (
              <PrimerMarkdownEditor.ActionButton variant="default" size={buttonSize} onClick={onCancel} value="Cancel">
                Cancel
              </PrimerMarkdownEditor.ActionButton>
            )}
            {onSaveAction &&
              validationResult &&
              saveButtonWithValidation(
                onSaveAction,
                validationResult,
                saveButtonText,
                saveButtonTrailingIcon,
                disabled,
                buttonSize,
              )}
          </PrimerMarkdownEditor.Actions>
          <PrimerMarkdownEditor.Toolbar>
            <PrimerMarkdownEditor.DefaultToolbarButtons />
            <SlashCommandsButton />
            {showAddTaskListButton && <AddTasklistButton editedBody={value} onChangeBody={onChange} />}
          </PrimerMarkdownEditor.Toolbar>
        </PrimerMarkdownEditor>
      </Suspense>
    )

    return (
      <div>
        {!isProjectSubject && subject?.repository?.slashCommandsEnabled ? (
          <SlashCommandButtonContext.Provider value={onStartSlashCommand}>
            <SlashCommandsProvider subject={subject}>{editor}</SlashCommandsProvider>
          </SlashCommandButtonContext.Provider>
        ) : (
          editor
        )}
      </div>
    )
  },
)

const {ToolbarButton} = PrimerMarkdownEditor

const SlashCommandsButton = () => {
  const onStartSlashCommand = useContext(SlashCommandButtonContext)
  return onStartSlashCommand ? (
    <ToolbarButton icon={DiffIgnoredIcon} aria-label="Slash commands" onClick={onStartSlashCommand} />
  ) : null
}

MarkdownEditorWrapper.displayName = 'MarkdownEditorWrapper'

function saveButtonWithValidation(
  onSave: () => void,
  validationResult: ValidationResult,
  saveButtonText: string,
  trailingIcon: boolean,
  disabled: boolean,
  size?: 'small' | 'medium',
) {
  if (validationResult.isValid) {
    return saveButton(onSave, disabled, saveButtonText, trailingIcon, size)
  }
  return (
    <Tooltip text={validationResult.errorMessage} direction="w" data-testid="save-button-tooltip">
      {saveButton(onSave, true, saveButtonText, trailingIcon, size)}
    </Tooltip>
  )
}

function saveButton(
  onSave: () => void,
  disabled: boolean,
  saveButtonText: string,
  trailingIcon: boolean,
  size: 'small' | 'medium' = 'medium',
) {
  return (
    <PrimerMarkdownEditor.ActionButton
      variant="primary"
      size={size}
      onClick={onSave}
      disabled={disabled}
      trailingVisual={trailingIcon && disabled ? AlertFillIcon : null}
    >
      {saveButtonText}
    </PrimerMarkdownEditor.ActionButton>
  )
}

export function ReadonlyMarkdownEditor({reason, icon}: ReadonlyMarkdownProps) {
  return (
    <Box
      className="blankslate"
      sx={{
        border: '1px solid',
        borderColor: 'neutral.muted',
        borderRadius: 2,
        backgroundColor: 'canvas.default',
        boxShadow: 'shadow.small',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'fg.muted',
        }}
      >
        {icon && icon({size: 24})}
        <Text sx={{mt: 2}}>{reason}</Text>
      </Box>
    </Box>
  )
}

try{ SlashCommandButtonContext.displayName ||= 'SlashCommandButtonContext' } catch {}
try{ SlashCommandsButton.displayName ||= 'SlashCommandsButton' } catch {}
try{ ReadonlyMarkdownEditor.displayName ||= 'ReadonlyMarkdownEditor' } catch {}